import { useEffect, useState } from "react";
import { ArrowLeft, KeyRound, LogIn, MailCheck, UserPlus, X } from "lucide-react";
import { apiJson } from "../api";
import type { AccountUser } from "../types";

type Mode = "login" | "register" | "verify" | "forgot" | "reset";

type Props = {
  open: boolean;
  onClose: () => void;
  onAuthenticated: (user: AccountUser) => void;
};

export function AuthModal({ open, onClose, onAuthenticated }: Props) {
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [debugCode, setDebugCode] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(false);

  useEffect(() => {
    if (!open) {
      setError("");
      setMessage("");
      setDebugCode("");
    }
  }, [open]);

  if (!open) return null;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setMessage("");
    setSubmitting(true);
    try {
      if (mode === "login") {
        const result = await apiJson<{ user: AccountUser }>("/api/auth/login", {
          method: "POST",
          body: JSON.stringify({ email, password })
        });
        onAuthenticated(result.user);
        onClose();
      }
      if (mode === "register") {
        if (!legalAccepted) {
          throw new Error("请先阅读并同意用户服务协议和隐私政策");
        }
        const result = await apiJson<{ debugCode?: string; message: string }>(
          "/api/auth/register",
          {
            method: "POST",
            body: JSON.stringify({ email, password })
          }
        );
        setDebugCode(result.debugCode || "");
        setCode(result.debugCode || "");
        setMessage(result.message);
        setMode("verify");
      }
      if (mode === "verify") {
        const result = await apiJson<{ user: AccountUser }>("/api/auth/verify-email", {
          method: "POST",
          body: JSON.stringify({ email, code })
        });
        onAuthenticated(result.user);
        onClose();
      }
      if (mode === "forgot") {
        const result = await apiJson<{ debugCode?: string; message: string }>(
          "/api/auth/forgot-password",
          {
            method: "POST",
            body: JSON.stringify({ email })
          }
        );
        setDebugCode(result.debugCode || "");
        setCode(result.debugCode || "");
        setMessage(result.message);
        setMode("reset");
      }
      if (mode === "reset") {
        const result = await apiJson<{ message: string }>("/api/auth/reset-password", {
          method: "POST",
          body: JSON.stringify({ email, code, password })
        });
        setMessage(result.message);
        setMode("login");
      }
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : "操作失败");
    } finally {
      setSubmitting(false);
    }
  };

  const title = {
    login: "登录账户",
    register: "创建账户",
    verify: "验证邮箱",
    forgot: "找回密码",
    reset: "设置新密码"
  }[mode];

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="account-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-modal-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="modal-header">
          <div>
            <span className="eyebrow">账户中心</span>
            <h2 id="auth-modal-title">{title}</h2>
          </div>
          <button className="modal-close" type="button" onClick={onClose} aria-label="关闭">
            <X size={20} />
          </button>
        </header>

        <form className="auth-form" onSubmit={submit}>
          {mode !== "login" && (
            <button
              className="auth-back"
              type="button"
              onClick={() => {
                setMode("login");
                setError("");
                setMessage("");
              }}
            >
              <ArrowLeft size={15} />
              返回登录
            </button>
          )}

          <label>
            <span>邮箱</span>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="name@example.com"
              disabled={mode === "verify" || mode === "reset"}
            />
          </label>

          {(mode === "login" || mode === "register" || mode === "reset") && (
            <label>
              <span>{mode === "reset" ? "新密码" : "密码"}</span>
              <input
                type="password"
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                required
                minLength={8}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="至少 8 位，包含字母和数字或符号"
              />
            </label>
          )}

          {(mode === "verify" || mode === "reset") && (
            <label>
              <span>6 位验证码</span>
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                maxLength={6}
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
                placeholder="邮件中的验证码"
              />
            </label>
          )}

          {debugCode && (
            <p className="debug-code" role="note">
              开发环境验证码：<strong>{debugCode}</strong>
            </p>
          )}
          {message && <p className="form-message">{message}</p>}
          {error && <p className="composer-error" role="alert">{error}</p>}

          <button className="auth-submit" type="submit" disabled={submitting}>
            {mode === "login" && <LogIn size={18} />}
            {mode === "register" && <UserPlus size={18} />}
            {mode === "verify" && <MailCheck size={18} />}
            {(mode === "forgot" || mode === "reset") && <KeyRound size={18} />}
            {submitting ? "处理中…" : title}
          </button>

          {mode === "login" && (
            <div className="auth-links">
              <button type="button" onClick={() => setMode("register")}>注册邮箱账户</button>
              <button type="button" onClick={() => setMode("forgot")}>忘记密码</button>
            </div>
          )}
          {mode === "register" && (
            <label className="legal-consent">
              <input
                type="checkbox"
                checked={legalAccepted}
                onChange={(event) => setLegalAccepted(event.target.checked)}
              />
              <span>
                我已阅读并同意
                <a href="/terms.html" target="_blank" rel="noreferrer">用户服务协议</a>
                和
                <a href="/privacy.html" target="_blank" rel="noreferrer">隐私政策</a>。
              </span>
            </label>
          )}
        </form>
      </section>
    </div>
  );
}
